import React from "react";
import { createPortal } from "react-dom";

import { useMarkerLibrary } from "./GoogleAPIProvider";
import { useMapContext } from "./GoogleMapsProvider";

interface AdvancedMarkerEventProps {
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onDrag?: (e: google.maps.MapMouseEvent) => void;
  onDragStart?: (e: google.maps.MapMouseEvent) => void;
  onDragEnd?: (e: google.maps.MapMouseEvent) => void;
  onMouseEnter?: (e: google.maps.MapMouseEvent) => void;
  onMouseLeave?: (e: google.maps.MapMouseEvent) => void;
}

export type AdvancedMarkerProps = React.PropsWithChildren<
  Omit<
    google.maps.marker.AdvancedMarkerElementOptions,
    "gmpDraggable" | "gmpClickable" | "content" | "map" | "collisionBehavior"
  > &
    AdvancedMarkerEventProps
>;

export const AdvancedMarker = ({
  position,
  children,
  onClick,
  onDrag,
  onDragEnd,
  onDragStart,
  onMouseEnter,
  onMouseLeave,
  zIndex
}: AdvancedMarkerProps) => {
  const [marker, setMarker] = React.useState<google.maps.marker.AdvancedMarkerElement | null>(null);
  const contentContainerRef = React.useRef<HTMLDivElement | null>(null);
  const markerLibrary = useMarkerLibrary();
  const { map } = useMapContext();

  // put these props in a ref to avoid inflating the deps array
  const positionRef = React.useRef(position);
  const zIndexRef = React.useRef(zIndex);

  // Marker creation and initial setup
  React.useEffect(() => {
    if (!markerLibrary || !map) {
      return;
    }

    const markerElement = new markerLibrary.AdvancedMarkerElement({
      map,
      position: positionRef.current,
      zIndex: zIndexRef.current || null
    });

    // Create a content container for the marker so that it is rendered on first load
    const contentContainer = document.createElement("div");
    markerElement.content = contentContainer;
    contentContainerRef.current = contentContainer;

    setMarker(markerElement);

    return () => {
      markerElement.map = null;
      setMarker(null);
      contentContainerRef.current = null;
    };
  }, [markerLibrary, map]);

  // Update marker position and zIndex
  React.useEffect(() => {
    if (marker) {
      marker.position = position;
      marker.zIndex = zIndex || null;
    }
  }, [marker, position, zIndex]);

  // Add event listeners
  React.useEffect(() => {
    if (!marker) {
      return;
    }

    if (onClick) {
      marker.addListener("click", onClick);
    }
    if (onDrag) {
      marker.addListener("drag", onDrag);
    }
    if (onDragStart) {
      marker.addListener("dragstart", onDragStart);
    }
    if (onDragEnd) {
      marker.addListener("dragend", onDragEnd);
    }
    if (onMouseEnter) {
      marker.addListener("mouseover", onMouseEnter);
    }
    if (onMouseLeave) {
      marker.addListener("mouseout", onMouseLeave);
    }
    return () => {
      google.maps.event.clearInstanceListeners(marker);
    };
  }, [marker, onClick, onDrag, onDragStart, onDragEnd, onMouseEnter, onMouseLeave]);

  // Handle children (content) updates
  React.useEffect(() => {
    if (marker && children && !contentContainerRef.current) {
      const contentContainer = document.createElement("div");
      marker.content = contentContainer;
      contentContainerRef.current = contentContainer;
    }
  }, [children, marker, contentContainerRef]);

  if (!contentContainerRef.current) {
    return null;
  }

  return createPortal(children, contentContainerRef.current);
};

AdvancedMarker.displayName = "AdvancedMarker";
